import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import macro from '../../macros.js';
import vtkAbstractWidgetFactory from '../Core/AbstractWidgetFactory.js';
import vtkImplicitPlaneRepresentation from '../Representations/ImplicitPlaneRepresentation.js';
import vtkLineManipulator from '../Manipulators/LineManipulator.js';
import vtkTrackballManipulator from '../Manipulators/TrackballManipulator.js';
import vtkPlanePointManipulator from '../Manipulators/PlaneManipulator.js';
import { ViewTypes } from '../Core/WidgetManager/Constants.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
// Widget linked to a view
// ----------------------------------------------------------------------------

function widgetBehavior(publicAPI, model) {
  model.classHierarchy.push('vtkPlaneWidget');
  model._isDragging = false;

  publicAPI.setDisplayCallback = function (callback) {
    return model.representations[0].setDisplayCallback(callback);
  };

  publicAPI.updateCursor = function () {
    switch (model.activeState.getUpdateMethodName()) {
      case 'updateFromOrigin':
        model._apiSpecificRenderWindow.setCursor('crosshair');

        break;

      case 'updateFromPlane':
        model._apiSpecificRenderWindow.setCursor('move');

        break;

      case 'updateFromNormal':
        model._apiSpecificRenderWindow.setCursor('alias');

        break;

      default:
        model._apiSpecificRenderWindow.setCursor('grabbing');

        break;
    }
  };

  publicAPI.handleLeftButtonPress = function (callData) {
    if (!model.activeState || !model.activeState.getActive() || !model.pickable) {
      return macro.VOID;
    }

    model.lineManipulator.setWidgetOrigin(model.widgetState.getOrigin());
    model.planeManipulator.setWidgetOrigin(model.widgetState.getOrigin());
    model.trackballManipulator.reset(callData); // setup trackball delta

    if (model.dragable) {
      model._isDragging = true;

      model._apiSpecificRenderWindow.setCursor('grabbing');

      model._interactor.requestAnimation(publicAPI);
    }

    publicAPI.invokeStartInteractionEvent();
    return macro.EVENT_ABORT;
  };

  publicAPI.handleMouseMove = function (callData) {
    if (model._isDragging) {
      return publicAPI.handleEvent(callData);
    }

    return macro.VOID;
  };

  publicAPI.handleLeftButtonRelease = function () {
    if (!model.activeState || !model.activeState.getActive() || !model.pickable) {
      return macro.VOID;
    }

    if (model._isDragging) {
      model._interactor.cancelAnimation(publicAPI);

      model._isDragging = false;
    }

    model.widgetState.deactivate();
    publicAPI.invokeEndInteractionEvent();
    return macro.EVENT_ABORT;
  };

  publicAPI.handleEvent = function (callData) {
    if (model.pickable && model.activeState && model.activeState.getActive()) {
      publicAPI[model.activeState.getUpdateMethodName()](callData);
      publicAPI.invokeInteractionEvent();
      return macro.EVENT_ABORT;
    }

    return macro.VOID;
  }; // --------------------------------------------------------------------------
  // Event coordinate translation
  // --------------------------------------------------------------------------


  publicAPI.updateFromOrigin = function (callData) {
    model.planeManipulator.setWidgetNormal(model.widgetState.getNormal());
    var worldCoords = model.planeManipulator.handleEvent(callData, model._apiSpecificRenderWindow);

    if (model.widgetState.containsPoint(worldCoords)) {
      model.activeState.setOrigin(worldCoords);
    }
  }; // --------------------------------------------------------------------------


  publicAPI.updateFromPlane = function (callData) {
    var _model$widgetState;

    // Move origin along normal axis
    model.lineManipulator.setWidgetNormal(model.activeState.getNormal());
    var worldCoords = model.lineManipulator.handleEvent(callData, model._apiSpecificRenderWindow);

    if ((_model$widgetState = model.widgetState).containsPoint.apply(_model$widgetState, _toConsumableArray(worldCoords))) {
      model.activeState.setOrigin(worldCoords);
    }
  }; // --------------------------------------------------------------------------


  publicAPI.updateFromNormal = function (callData) {
    model.trackballManipulator.setWidgetNormal(model.activeState.getNormal());
    var newNormal = model.trackballManipulator.handleEvent(callData, model._apiSpecificRenderWindow);
    model.activeState.setNormal(newNormal);
  }; // --------------------------------------------------------------------------
  // initialization
  // --------------------------------------------------------------------------


  model.lineManipulator = vtkLineManipulator.newInstance();
  model.planeManipulator = vtkPlanePointManipulator.newInstance();
  model.trackballManipulator = vtkTrackballManipulator.newInstance();
} // ----------------------------------------------------------------------------
// Factory
// ----------------------------------------------------------------------------


function vtkImplicitPlaneWidget(publicAPI, model) {
  model.classHierarchy.push('vtkPlaneWidget'); // --- Widget Requirement ---------------------------------------------------

  model.methodsToLink = ['representationStyle', 'sphereResolution', 'handleSizeRatio', 'axisScale', 'normalVisible', 'originVisible', 'planeVisible', 'outlineVisible'];

  publicAPI.getRepresentationsForViewType = function (viewType) {
    switch (viewType) {
      case ViewTypes.DEFAULT:
      case ViewTypes.GEOMETRY:
      case ViewTypes.SLICE:
      case ViewTypes.VOLUME:
      default:
        return [{
          builder: vtkImplicitPlaneRepresentation
        }];
    }
  };
} // ----------------------------------------------------------------------------


var defaultValues = function defaultValues(initialValues) {
  return _objectSpread({
    behavior: widgetBehavior,
    widgetState: vtkImplicitPlaneRepresentation.generateState()
  }, initialValues);
}; // ----------------------------------------------------------------------------


function extend(publicAPI, model) {
  var initialValues = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  Object.assign(model, defaultValues(initialValues));
  vtkAbstractWidgetFactory.extend(publicAPI, model, initialValues);
  vtkImplicitPlaneWidget(publicAPI, model);
} // ----------------------------------------------------------------------------

var newInstance = macro.newInstance(extend, 'vtkImplicitPlaneWidget'); // ----------------------------------------------------------------------------

var vtkImplicitPlaneWidget$1 = {
  newInstance: newInstance,
  extend: extend
};

export { vtkImplicitPlaneWidget$1 as default, extend, newInstance };
